import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import CalendarHeader from './components/CalendarHeader';
import MiniCalendar from './components/MiniCalendar';
import ShiftLegend from './components/ShiftLegend';
import AvailabilityControls from './components/AvailabilityControls';
import UpcomingShifts from './components/UpcomingShifts';
import CalendarGrid from './components/CalendarGrid';
import ShiftDetailsPanel from './components/ShiftDetailsPanel';
import BulkOperationsToolbar from './components/BulkOperationsToolbar';
import FilterControls from './components/FilterControls';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const EmployeeShiftCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [filters, setFilters] = useState({});
  const [savedFilters, setSavedFilters] = useState([]);
  const [showShiftDetails, setShowShiftDetails] = useState(false);

  // Mock shift data
  const [shifts] = useState([
    {
      id: 1,
      department: 'Kundenservice',
      position: 'Kundenberater',
      location: 'Hauptgebäude',
      startTime: new Date(2025, 9, 5, 8, 0),
      endTime: new Date(2025, 9, 5, 16, 0),
      status: 'assigned',
      requirements: 'Deutschkenntnisse erforderlich',
      notes: 'Wichtiger Kunde erwartet',
      hasConflict: false
    },
    {
      id: 2,
      department: 'Betrieb',
      position: 'Techniker',
      location: 'Lager',
      startTime: new Date(2025, 9, 6, 14, 0),
      endTime: new Date(2025, 9, 6, 22, 0),
      status: 'available',
      requirements: 'Technische Ausbildung',
      notes: '',
      hasConflict: false,
      requiresApproval: true
    },
    {
      id: 3,
      department: 'Sicherheit',
      position: 'Sicherheitsdienst',
      location: 'Empfang',
      startTime: new Date(2025, 9, 7, 22, 0),
      endTime: new Date(2025, 9, 8, 6, 0),
      status: 'requested',
      requirements: 'Sicherheitszertifikat',
      notes: 'Nachtschicht',
      hasConflict: false
    },
    {
      id: 4,
      department: 'Wartung',
      position: 'Hausmeister',
      location: 'Hauptgebäude',
      startTime: new Date(2025, 9, 8, 6, 0),
      endTime: new Date(2025, 9, 8, 14, 0),
      status: 'available',
      requirements: 'Handwerkliche Erfahrung',
      notes: 'Routinewartung',
      hasConflict: false
    },
    {
      id: 5,
      department: 'Verwaltung',
      position: 'Büroassistent',
      location: 'Verwaltung',
      startTime: new Date(2025, 9, 9, 9, 0),
      endTime: new Date(2025, 9, 9, 17, 0),
      status: 'unavailable',
      requirements: 'MS Office Kenntnisse',
      notes: 'Bereits besetzt',
      hasConflict: false
    },
    {
      id: 6,
      department: 'Kundenservice',
      position: 'Telefonist',
      location: 'Call Center',
      startTime: new Date(2025, 9, 10, 10, 0),
      endTime: new Date(2025, 9, 10, 18, 0),
      status: 'assigned',
      requirements: 'Kommunikationsfähigkeiten',
      notes: 'Hochfrequentierter Tag',
      hasConflict: true
    }
  ]);

  // Handle navigation
  const handleNavigate = (direction) => {
    const newDate = new Date(currentDate);
    
    if (viewMode === 'month') {
      newDate?.setMonth(newDate?.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate?.setDate(newDate?.getDate() + (direction === 'next' ? 7 : -7));
    } else if (viewMode === 'day') {
      newDate?.setDate(newDate?.getDate() + (direction === 'next' ? 1 : -1));
    }
    
    setCurrentDate(newDate);
  };

  const handleTodayClick = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setCurrentDate(date);
  };

  const handleShiftClick = (shift) => {
    setSelectedShift(shift);
    setShowShiftDetails(true);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedShift(null);
  };

  const handleAvailabilityChange = (availability) => {
    console.log('Verfügbarkeit geändert:', availability);
    // In real app, this would update the backend
  };

  const handleBookShift = (shift) => {
    console.log('Schicht buchen:', shift);
    // In real app, this would make an API call
  };

  const handleCancelShift = (shift) => {
    console.log('Schicht stornieren:', shift);
    // In real app, this would make an API call
  };

  const handleRequestShift = (shift) => {
    console.log('Schicht anfragen:', shift);
    // In real app, this would make an API call
  };

  const handleBulkBook = (shifts) => {
    console.log('Bulk-Buchung:', shifts);
    setSelectedShifts([]);
  };

  const handleBulkCancel = (shifts) => {
    console.log('Bulk-Stornierung:', shifts);
    setSelectedShifts([]);
  };

  const handleBulkAvailability = (settings) => {
    console.log('Bulk-Verfügbarkeit:', settings);
    setSelectedShifts([]);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSaveFilter = (name, filterSettings) => {
    const newFilter = {
      id: Date.now(),
      name,
      filters: filterSettings
    };
    setSavedFilters(prev => [...prev, newFilter]);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event?.target?.tagName === 'INPUT' || event?.target?.tagName === 'TEXTAREA') {
        return;
      }

      switch (event?.key) {
        case 'ArrowLeft':
          event?.preventDefault();
          handleNavigate('prev');
          break;
        case 'ArrowRight':
          event?.preventDefault();
          handleNavigate('next');
          break;
        case ' ':
          event?.preventDefault();
          if (selectedShift && selectedShift?.status === 'available') {
            handleBookShift(selectedShift);
          }
          break;
        case 'Escape':
          event?.preventDefault();
          setSelectedShift(null);
          setShowShiftDetails(false);
          break;
        case 't':
          event?.preventDefault();
          handleTodayClick();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedShift]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-full mx-auto p-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Schichtkalender</h1>
              <p className="text-muted-foreground">
                Verwalten Sie Ihre Schichten und Verfügbarkeit
              </p>
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                iconName="Calendar"
                iconPosition="left"
                iconSize={16}
                onClick={() => window.location.href = '/available-shifts-booking'}
              >
                Schichten buchen
              </Button>
              <Button
                variant="outline"
                iconName="Settings"
                iconPosition="left"
                iconSize={16}
                onClick={() => window.location.href = '/personal-availability-management'}
              >
                Verfügbarkeit
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                iconSize={16}
                onClick={() => window.location.href = '/shift-creation-and-editing'}
              >
                Neue Schicht
              </Button>
            </div>
          </div>

          {/* Filters */}
          <FilterControls
            onFiltersChange={handleFiltersChange}
            savedFilters={savedFilters}
            onSaveFilter={handleSaveFilter}
          />

          {/* Bulk Operations */}
          <BulkOperationsToolbar
            selectedShifts={selectedShifts}
            onBulkBook={handleBulkBook}
            onBulkCancel={handleBulkCancel}
            onBulkAvailability={handleBulkAvailability}
            onClearSelection={() => setSelectedShifts([])}
          />

          {/* Main Content */}
          <div className="flex gap-6">
            {/* Left Sidebar */}
            <div className="w-80 space-y-6">
              <MiniCalendar
                currentDate={currentDate}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
              />
              
              <ShiftLegend />
              
              <AvailabilityControls
                onAvailabilityChange={handleAvailabilityChange}
              />
              
              <UpcomingShifts
                shifts={shifts}
                onShiftClick={handleShiftClick}
              />
            </div>

            {/* Main Calendar */}
            <div className="flex-1">
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <CalendarHeader
                  currentDate={currentDate}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  onNavigate={handleNavigate}
                  onTodayClick={handleTodayClick}
                />
                
                <CalendarGrid
                  currentDate={currentDate}
                  viewMode={viewMode}
                  shifts={shifts}
                  onShiftClick={handleShiftClick}
                  onDateClick={handleDateClick}
                  selectedDate={selectedDate}
                />
              </div>
            </div>

            {/* Right Panel */}
            {showShiftDetails && (
              <ShiftDetailsPanel
                selectedShift={selectedShift}
                onClose={() => {
                  setSelectedShift(null);
                  setShowShiftDetails(false);
                }}
                onBookShift={handleBookShift}
                onCancelShift={handleCancelShift}
                onRequestShift={handleRequestShift}
              />
            )}
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="mt-8 p-4 bg-muted rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Keyboard" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Tastaturkürzel</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-muted-foreground">
              <div>← → Navigation</div>
              <div>Leertaste: Buchen</div>
              <div>Esc: Schließen</div>
              <div>T: Heute</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeeShiftCalendar;